import { RxCheckCircled } from '@react-icons/all-files/rx/RxCheckCircled'
import { RxCrossCircled } from '@react-icons/all-files/rx/RxCrossCircled'
import { RxExternalLink } from '@react-icons/all-files/rx/RxExternalLink'
import React, { useMemo } from 'react'

import type { BulkSaveTrackResult } from '@disco/state-management/reducers/rightsManagement'
import { getBulkSaveResults } from '@disco/state-management/reducers/rightsManagement'
import { getTrack } from '@disco/state-management/selectors/tracks'

import {
  Box,
  Button,
  Dialog,
  Flex,
  Heading,
  Separator,
  Text,
} from 'components/ds'

import { useSelector } from 'hooks/use-redux'

import styles from './review-track-metadata-modal.module.css'

function FailedTrackRow({ result }: { result: BulkSaveTrackResult }) {
  const track = useSelector(state => {
    if (!state.tracks || result.trackId == null) return null
    return getTrack(state, { id: result.trackId })
  })

  const trackName = track?.name || 'Unknown track'
  const errorMessages = result.errors || [result.error || 'Unknown error']
  const trackUrl =
    result.trackId != null
      ? `/explorer/tracks/${result.trackId}/edit-metadata`
      : null

  return (
    <Box>
      <Flex justify="between" align="center" gap="3" py="2">
        <Box>
          <Text size="2">
            {trackUrl ? (
              <a
                href={trackUrl}
                target="_blank"
                rel="noopener noreferrer"
                className={styles.trackLink}
              >
                {trackName}
                <RxExternalLink size={16} className={styles.externalLinkIcon} />
              </a>
            ) : (
              trackName
            )}
          </Text>
          {errorMessages.map((error, index) => (
            <Text
              as="p"
              key={`${error}-${index}`}
              size="1"
              className={styles.errorMessage}
            >
              {error}
            </Text>
          ))}
        </Box>
        <RxCrossCircled className={styles.errorIcon} size={16} />
      </Flex>
      <Separator size="4" />
    </Box>
  )
}

function SuccessTrackRow({ result }: { result: BulkSaveTrackResult }) {
  const track = useSelector(state => {
    if (!state.tracks || result.trackId == null) return null
    return getTrack(state, { id: result.trackId })
  })

  const trackName = track?.name || 'Unknown track'
  const artistName = track?.artist

  return (
    <Box>
      <Flex justify="between" align="center" gap="3" py="2">
        <Box>
          <Text size="2">{trackName}</Text>
          {artistName && (
            <Text as="p" size="1" color="gray">
              {artistName}
            </Text>
          )}
        </Box>
        <RxCheckCircled className={styles.successIcon} size={16} />
      </Flex>
      <Separator size="4" />
    </Box>
  )
}

interface ReviewTrackMetadataModalProps {
  open: boolean
  onClose: () => void
}

export function ReviewTrackMetadataModal({
  open,
  onClose,
}: ReviewTrackMetadataModalProps) {
  const results = useSelector(getBulkSaveResults)

  // Sort: failures first, then successes
  const sortedResults = useMemo(
    () =>
      [...results].sort((a, b) => {
        if (a.success === b.success) return 0
        return a.success ? 1 : -1
      }),
    [results],
  )

  const hasSuccesses = sortedResults.some(r => r.success)

  return (
    <Dialog.Root
      open={open}
      onOpenChange={isOpen => {
        if (!isOpen) onClose()
      }}
    >
      <Dialog.Content maxWidth="450px">
        {/* <Heading size="5" mb="3">
          Review track metadata
        </Heading> */}
        <Dialog.Title>Review track metadata</Dialog.Title>
        <Dialog.Description size="2" mb="3">
          {hasSuccesses
            ? 'Some tracks could not be updated. Please review the status below.'
            : 'The tracks could not be updated. Please review the errors below.'}
        </Dialog.Description>
        <Box className={styles.trackList}>
          {sortedResults.map(result =>
            result.success ? (
              <SuccessTrackRow key={result.trackId} result={result} />
            ) : (
              <FailedTrackRow key={result.trackId} result={result} />
            ),
          )}
        </Box>
        <Flex justify="end" mt="4">
          <Dialog.Close>
            <Button>Close</Button>
          </Dialog.Close>
        </Flex>
      </Dialog.Content>
    </Dialog.Root>
  )
}
